import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { API_URL } from '../const'
import {
  CategoryDomain,
  Monument,
  MonumentList,
  PaginatedDJResponse,
  UploadedImage,
} from '../types'
import axios from 'axios'
import { getNextPageParam, serializeQueryParams } from './utils'
import { ImageInfo } from '../components/Mobile/BlockUpload/BlockUpload'


export async function getMonuments(
  params: Record<string, any> = {},
  signal?: AbortSignal
) {
  return (
    await axios.get(`${API_URL}/monuments/`, {
      signal,
      params: serializeQueryParams(params),
    })
  ).data as PaginatedDJResponse<MonumentList>
}

export function useMonuments(params: Record<string, any> = {}) {
  return useQuery({
    queryKey: ['monuments', params],
    queryFn: ({ signal }) => getMonuments(params, signal),
    keepPreviousData: true,
  })
}

export function useInfiniteMomuments(params: Record<string, any> = {}, enabled = true) {
  return useInfiniteQuery({
    queryKey: ['infiniteMonuments', params],
    queryFn: ({ signal, pageParam }) =>
      getMonuments(
        {
          ...params,
          ...pageParam,
        },
        signal
      ),
    getNextPageParam: getNextPageParam,
    keepPreviousData: true,
    cacheTime: 1000 * 60 * 6,
    staleTime: 300,
    enabled,
  })
}

export async function getMonument(
  idOrSlug: string | number,
  token: string,
  isDesktop: boolean,
  signal?: AbortSignal
) {
  const platform = isDesktop ? 'desktop' : 'mobile'
  let url = `${API_URL}/monuments/${idOrSlug}/?platform=${platform}`
  const axiosConfig = {
    signal
  }  as any
  if(token) {
    axiosConfig.headers = { 'Authorization': `Bearer ${token}` }
  }

  return (await axios.get(url, axiosConfig)).data as Monument
}

export function useMonument(idOrSlug: string | number, token: string, isDesktop = true) {
  return useQuery({
    queryKey: ['monument', idOrSlug],
    queryFn: ({ signal }) => getMonument(idOrSlug, token, isDesktop, signal),
  }).data!
}

export async function getCategoriesDomain(signal?: AbortSignal) {
  return (
    await axios.get(`${API_URL}/categories-domain/`, {
      signal,
    })
  ).data as CategoryDomain[]
}

export function useCategoriesDomain() {
  return useQuery({
    queryKey: ['categories-domain'],
    queryFn: () => getCategoriesDomain(),
    suspense: false,
  })
}

export async function uploadImages(images: ImageInfo[], platform: string, token?: string, currentNonWlmCat?: string[]) {
  const formData = new FormData()

  images.forEach((image, i) => {
    formData.append(`images[${i}]image`, image.file as Blob)
    formData.append(`images[${i}]title`, image.title)
    formData.append(`images[${i}]description`, image.description)
    formData.append(`images[${i}]date`, image.date)
    formData.append(`images[${i}]monument_id`, String(image.monument_id))
    formData.append(`images[${i}]typology_view_exterior`, image.typology_view_exterior ? 'true' : 'false')
    formData.append(`images[${i}]typology_view_interior`, image.typology_view_interior ? 'true' : 'false')
    if (image.film && image.film !== '') {
      formData.append(`images[${i}]film`, String(image.film))
    }
    if (image.latitude !== null) {
      formData.append(`images[${i}]lat`, String(image.latitude))
    }
    if (image.longitude !== null) {
      formData.append(`images[${i}]lon`, String(image.longitude))
    }
    if (image.heading !== null) {
      formData.append(`images[${i}]heading`, String(image.heading))
    }
    if (image.extraCategories) {
      formData.append(`images[${i}]categories`, JSON.stringify(image.extraCategories))
    }
    if (currentNonWlmCat && currentNonWlmCat[i]) {
      formData.append(`images[${i}]main_category`, currentNonWlmCat[i])
    }
  })
  formData.append('platform', platform)
  return (
    await axios.post(`${API_URL}/upload-images/`, formData, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })
  )
}

export async function enableMonument(id: number, token: string) {
  return (
    await axios.post(`${API_URL}/monuments/${id}/enable/`, null, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })
  )
}


export async function disableMonument(id: number, token: string) {
  return (
    await axios.post(`${API_URL}/monuments/${id}/disable/`, null, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })
  )
}

export async function updateMonument(id: number, currentUpdateQuery: number | null, token: string) {
  return (
    await axios.post(`${API_URL}/monuments/${id}/update_data/`, null, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      params: {
        update_query_id: currentUpdateQuery,
      },
    })
  )
}


export async function personalImages(token: string) {
  return (
    await axios.get<UploadedImage[]>(`${API_URL}/personal-contributions/`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })
  )
}

export async function categoryTree(cat: string, token: string) {
  return (
    await axios.get(`${API_URL}/categories-tree?cat=${cat}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })
  ).data
}